import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { PHONE_REGEX } from 'src/utils/regex'
import addressRequest from 'src/api/requests/address'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { useAppDispatch } from 'src/store'
import { register } from 'src/useCase/user'
import appActions from 'src/store/ui/app'

export type SMSInputFormValues = {
  phone: string
}

export const useSMSInputForm = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const menuId = searchParams.get('menu_id') || searchParams.get('menuId') || ''
  const registrationData = location.state?.registrationData
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)

  const defaultValues: SMSInputFormValues = {
    phone: '',
  }

  const validationSchema = Yup.object().shape({
    phone: Yup.string()
      .required('入力してください。')
      .length(11, '携帯番号は11桁で入力してください。')
      .matches(PHONE_REGEX, '電話番号は０から始まる必要があります。'),
  })

  const form = useForm<SMSInputFormValues>({
    defaultValues,
    shouldFocusError: true,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  const handleBlur = async (value: string) => {
    if (!value) return

    const { phone: phoneError } = form.formState.errors
    if (phoneError) return

    try {
      await addressRequest.verifyUser({ phone: value })
      form.clearErrors('phone')
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.errors?.phone?.[0] || err?.response?.data?.message

      const isDuplicateError = apiMessage?.includes('既に使われています')

      if (isDuplicateError) {
        setIsDuplicateModalOpen(true)
      }

      form.setError('phone', {
        type: 'manual',
        message: apiMessage,
      })
    }
  }

  const onSubmit = async (data: SMSInputFormValues) => {
    try {
      if (registrationData) {
        const combinedData = { ...registrationData, phone: data.phone }
        await dispatch(
          register({
            credentials: combinedData,
            meta: form as any,
            redirectPath: `${AUTOMATIC_PLAN_PATH.SMS_AUTH}?menu_id=${
              menuId || ''
            }&${searchParams.toString()}`,
          }),
        ).unwrap()
      } else {
        const res = await addressRequest.updatePhone({ phone: data.phone })
        if (res.data) {
          // update phone in user
          dispatch(
            appActions.patchAuthInfo({
              phone: res.data.phone,
              otp_expire_at: res.data.otp_expire_at,
              otp_verified_at: res.data.otp_verified_at,
            }),
          )
          const params = new URLSearchParams(searchParams.toString())
          if (menuId) params.set('menu_id', menuId)

          navigate(`${AUTOMATIC_PLAN_PATH.SMS_AUTH}?${params.toString()}`)
        }
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.errors?.phone?.[0] ||
        error?.response?.data?.message

      if (apiMessage) {
        form.setError('phone', {
          type: 'manual',
          message: apiMessage,
        })
      }
    }
  }

  return {
    form,
    onSubmit,
    handleBlur,
    isDuplicateModalOpen,
    setIsDuplicateModalOpen,
  }
}
